import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Alert, Button, Input, Modal, Select, Space, Switch, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import type { McpAccessPerson } from '@/types/mcp'
import { useGrantMcpAccessMutation, useMcpAccess, usePendingMcpAccessUserIds, useRemoveMcpAccessMutation } from '../hooks'

type AccessFilter = 'all' | 'on' | 'off'

const accessFilterOptions: { label: string; value: AccessFilter }[] = [
  { label: 'All people', value: 'all' },
  { label: 'Access on', value: 'on' },
  { label: 'Access off', value: 'off' },
]

type PeopleTabProps = {
  clientId: string
  clientName: string
  onShowConnections: (person: McpAccessPerson) => void
}

export default function PeopleTab({ clientId, clientName, onShowConnections }: PeopleTabProps) {
  const { addToast } = useToast()
  const access = useMcpAccess(clientId)
  const grant = useGrantMcpAccessMutation(clientId)
  const remove = useRemoveMcpAccessMutation(clientId)
  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const people = useMemo(() => access.data ?? [], [access.data])
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return people.filter(
      (person) =>
        (!term || person.name.toLowerCase().includes(term) || person.email.toLowerCase().includes(term)) &&
        (accessFilter === 'all' || (accessFilter === 'on') === person.hasAccess)
    )
  }, [people, search, accessFilter])

  const selectable = useMemo(
    () => selectedIds.filter((id) => people.some((person) => person.userId === id && !person.hasAccess)),
    [selectedIds, people]
  )

  const pendingUserIds = usePendingMcpAccessUserIds(clientId)

  const giveAccess = async (userIds: string[], success: string) => {
    try {
      await grant.mutateAsync(userIds)
      setSelectedIds((current) => current.filter((id) => !userIds.includes(id)))
      addToast(success, 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to give MCP access', 'error')
    }
  }

  const confirmRemove = (person: McpAccessPerson) =>
    Modal.confirm({
      title: `Remove ${person.name}'s MCP access?`,
      content: `Any AI assistant they've connected to ${clientName} is disconnected straight away.`,
      okText: 'Remove access',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove.mutateAsync(person.userId)
          addToast(`Removed ${person.name}'s MCP access`, 'success')
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Unable to remove MCP access', 'error')
        }
      },
    })

  const columns: ColumnsType<McpAccessPerson> = [
    {
      title: 'Name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
      render: (_, person) => (
        <div>
          <div>{person.name}</div>
          <div className="text-xs text-gray-500">{person.email}</div>
        </div>
      ),
    },
    {
      title: 'MCP access',
      key: 'access',
      render: (_, person) => (
        <Switch
          checked={person.hasAccess}
          loading={pendingUserIds.has(person.userId)}
          aria-label={`MCP access for ${person.name}`}
          onChange={(next) =>
            next ? void giveAccess([person.userId], `Gave ${person.name} MCP access`) : confirmRemove(person)
          }
        />
      ),
    },
    {
      title: 'Given by',
      key: 'grantedBy',
      render: (_, person) =>
        person.hasAccess ? (
          <div>
            <div>{person.grantedBy || '-'}</div>
            {person.grantedAt && <div className="text-xs text-gray-500">{dayjs(person.grantedAt).format('DD/MM/YYYY')}</div>}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: 'Live connections',
      key: 'liveConnections',
      sorter: (a, b) => a.liveConnections - b.liveConnections,
      render: (_, person) =>
        person.liveConnections > 0 ? (
          <Button type="link" className="!p-0" onClick={() => onShowConnections(person)}>
            {person.liveConnections} live
          </Button>
        ) : (
          <span className="text-gray-400">0</span>
        ),
    },
  ]

  return (
    <div>
      <Space wrap className="mb-4">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={selectable.length === 0}
          loading={selectable.some((id) => pendingUserIds.has(id))}
          onClick={() =>
            void giveAccess(
              selectable,
              `Gave ${selectable.length} ${selectable.length === 1 ? 'person' : 'people'} MCP access`
            )
          }
        >
          {selectable.length > 0 ? `Give access (${selectable.length})` : 'Give access'}
        </Button>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email"
          suffix={<SearchOutlined />}
          allowClear
          aria-label="Search people"
          style={{ width: 220 }}
        />
        <Select
          value={accessFilter}
          onChange={setAccessFilter}
          options={accessFilterOptions}
          aria-label="Filter by MCP access"
          style={{ width: 150 }}
        />
        <Tooltip title="Refresh table">
          <Button icon={<SyncOutlined />} aria-label="Refresh table" onClick={() => void access.refetch()} />
        </Tooltip>
        {access.isFetching && !access.isLoading && (
          <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
            <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            Loading...
          </span>
        )}
      </Space>

      {access.isError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={access.error instanceof Error ? access.error.message : 'Failed to load people'}
        />
      )}

      <Table<McpAccessPerson>
        rowKey="userId"
        columns={columns}
        dataSource={rows}
        loading={access.isLoading}
        rowSelection={{
          selectedRowKeys: selectable,
          onChange: (keys) => setSelectedIds(keys.map(String)),
          getCheckboxProps: (person) => ({
            disabled: person.hasAccess,
            'aria-label': `Select ${person.name}`,
          }),
        }}
        locale={{
          emptyText: access.isError
            ? 'Couldn’t load people.'
            : people.length === 0
              ? `No one is linked to ${clientName} yet.`
              : 'No people match your search.',
        }}
        pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        scroll={{ x: 'max-content' }}
      />
      <p className="mt-2 text-xs text-gray-500">
        Only people without access can be selected. Remove access one person at a time.
      </p>
    </div>
  )
}
